import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import NotFoundPage from "../common/NotFoundPage";
import { getRoute } from "../../api/routes/routesApi";

// Links minted before Routes and Connections became separate screens put a
// ROUTE id under /connections/:id. Looking that id up as an integration 404s,
// so before showing the 404 page, try it as a Route: a hit means the bookmark
// meant the route, and the user is sent to its own URL. A miss (getRoute
// rethrows 404/403) means neither entity exists — the 404 page is correct.
// Only mounted after the connection lookup already failed, and only under
// PrivateRoute, so there is always a token.
export function ConnectionNotFoundFallback({ id }: { id?: string }) {
    const { keycloak } = useKeycloak();
    const [routeId, setRouteId] = useState<string | null>(null);
    const [checked, setChecked] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const token = keycloak?.token;
        const lookupRoute = async () => {
            if (!id || !token || typeof token !== "string" || keycloak.isTokenExpired()) {
                if (!cancelled) setChecked(true);
                return;
            }
            try {
                const route = await getRoute(token, id);
                if (!cancelled && route?.id) setRouteId(route.id);
            } catch (err) {
                // Not a route either — fall through to the 404 page.
            }
            if (!cancelled) setChecked(true);
        };
        lookupRoute();
        return () => { cancelled = true; };
    }, [id, keycloak]);

    if (routeId) return <Navigate to={`/routes/${routeId}`} replace />;
    // Nothing while the lookup runs: a 404 page that flips to a redirect reads
    // as a broken link the user briefly saw.
    if (!checked) return null;
    return <NotFoundPage />;
}
